
import { Component, Prop, Watch, Vue } from "vue-property-decorator";
import vuetifyToast from "vuetify-toast";
import DateTimePicker from "@/components/DatetimePicker.vue";
import DeductiblePaymentSelect from '@/components/DeductiblePaymentSelect.vue';
import DeductibleTypeSelect from '@/components/DeductibleTypeSelect.vue';
import Destination from "../models/Destination";
import Extra from "@/models/Extra";
import MovitInstanceConfig from "@/models/MovitInstanceConfig";
import Passenger from "../models/Passenger";
import { PassengerGroup } from "@/models/PassengerGroup";
import { IPriceArea } from "@/typings/timit";
import PriceAreaCard from "@/components/Passenger/components/PriceAreaCard.vue";
import PassengerTransportationPermissionNameInput from "@/components/Passenger/components/PassengerTransportationPermissionNameInput.vue";
import PassengerTransportationPermissionCostCenterInput from "@/components/Passenger/components/PassengerTransportationPermissionCostCenterInput.vue";
import PriceAreasTable from "@/models/PriceAreasTable";
import QuotaTimeSelect from '@/components/QuotaTimeSelect.vue';
import TransportationPermission, {
  DeductiblePaymentEnum,
  DeductibleTypeEnum,
} from "../models/TransportationPermission";
import TransportationPermissionExtra from "../models/TransportationPermissionExtra";
import TripDirectionsSelect from '@/components/TripDirectionsSelect.vue';
import TripDestinationsSelect from '@/components/TripDestinationsSelect.vue';
import QuotaTypeSelect from "@/components/QuotaTypeSelect.vue";

@Component({
  components: {
    DateTimePicker,
    DeductiblePaymentSelect,
    DeductibleTypeSelect,
    PriceAreaCard,
    PassengerTransportationPermissionNameInput,
    PassengerTransportationPermissionCostCenterInput,
    QuotaTimeSelect,
    TripDestinationsSelect,
    TripDirectionsSelect,
    QuotaTypeSelect,
  },
})
export default class TransportationPermissionEditor extends Vue {
  @Prop({ required: true }) private value!: TransportationPermission;
  @Prop({ default: false }) private showTransportationPermissionEditor!: boolean;
  @Prop({ default: false }) private hasPassengerGroups!: boolean;
  @Prop({ required: true }) private passenger!: Passenger;
  @Prop({ default: () => [] }) private passengerGroups!: PassengerGroup[];

  transportationPermission: TransportationPermission | null = null;
  DeductibleTypeEnum = DeductibleTypeEnum;
  DeductiblePaymentEnum = DeductiblePaymentEnum;
  lastEmitted!: TransportationPermission | null | undefined;
  adding: boolean = false;
  newExtra = { name: "", price: 0 };
  selectedExtra: Extra = null;
  showChangedIterator: number = 1;
  permissionIsValid: boolean = false;
  originalIsDraft = true;
  loading = false;

  rules: any = {
    required: val => !!val || this.$i18n.translate("validation.rules.required", "Required"),
    maxLength: (max: number) => (val: string) =>
      (val && val.length <= max) || this.$i18n.translate("validation.rules.maxLength", "Max length exceeded", [max]),
    hasToBeSetIfPriceInVatIsSet: (val) => this.checkVatPercent(val),
    notNegativeValue: val => val >= 0 || this.$i18n.translate("validation.rules.invalidValue", "Invalid value"),
    cannotExceedInitialQuota: (val) => this.cannotExceedInitialQuota(val),
  };

  checkVatPercent(val: number | null) {
    if(!this.transportationPermission.priceIncVat) {
      return true;
    }
    return (!!val || val === 0) || this.$i18n.translate("validation.rules.required", "Required");
  }

  cannotExceedInitialQuota(val: number) {
    if (!this.transportationPermission) {
      return true;
    }
    return (
      val <= this.transportationPermission.initialQuota ||
      Vue.i18n.translate("transportationPermission.expressQuotaExceedsQuota","Exceeds quota")
    );
  }

  @Watch("showTransportationPermissionEditor")
  showTransportationPermissionEditorChanged(val: boolean, oldVal: boolean) {
    if (val !== oldVal) {
      this.selectedExtra = null;
      this.adding = false;
      this.showChangedIterator++;
    }
  }

  @Watch("value", { immediate: true })
  onValueChange(val: any, oldVal: any) {
    if(!this.value) {
      // Closing editor when no permission is selected.
      // Tear down the working copy so the form and its child inputs (e.g. DateTimePicker) are destroyed via `v-if="transportationPermission"` 
      // and re-initialized fresh next time the editor opens, instead of reusing stale internal component state.
      this.transportationPermission = null;
      this.lastEmitted = null;
      this.selectedExtra = null;
      return null;
    }
    if (val !== this.lastEmitted) {
      if (this.value) {
        this.originalIsDraft = this.value.isDraft;
      }
      this.transportationPermission = TransportationPermission.CopyFrom(this.value);
      this.convertQuotaToUI();
    }
  }

  @Watch("transportationPermission", { deep: true })
  onTransportationPermissionChange() {
    if (this.transportationPermission && !this.transportationPermission.decisionType) {
      this.transportationPermission.decisionType = "TIMIT";
    }
    this.lastEmitted = this.transportationPermission;
    this.$emit("input", this.transportationPermission);
  }

  ConvertQuotaToDb() {
    switch(this.transportationPermission.quotaType) {
      case 1:
        if(this.destinationMovitInstanceConfig.isMileageInstance) {
          this.transportationPermission.initialQuota = this.transportationPermission.initialQuota * 1609.34;
          this.transportationPermission.initialExpressQuota = this.transportationPermission.initialExpressQuota * 1609.34;
        } else {
          this.transportationPermission.initialQuota = this.transportationPermission.initialQuota * 1000;
          this.transportationPermission.initialExpressQuota = this.transportationPermission.initialExpressQuota * 1000;
        }
        break;
      case 2:
        this.transportationPermission.initialQuota = this.transportationPermission.initialQuota * 100;
        this.transportationPermission.initialExpressQuota = this.transportationPermission.initialExpressQuota * 100;
        break;
      default:
        return;
    }
  }

  @Watch("transportationPermission.deductibleType")
  onDeductibleTypeChange(val: DeductibleTypeEnum) {
    if(val === DeductibleTypeEnum.FixedMonthly) {
      this.transportationPermission.deductiblePayment = DeductiblePaymentEnum.Invoice;
    }
  }


  get quotaFactorSuffix() {
    switch (this.transportationPermission.quotaTime) {
      case 0:
        return this.$i18n.translate("transportationPermission.quotaTimes.monthSuffix", "months");
      case 1:
        return this.$i18n.translate("transportationPermission.quotaTimes.daySuffix", "days");
      case 2:
        return this.$i18n.translate("transportationPermission.quotaTimes.weekSuffix", "weeks");
      case 3:
        return this.$i18n.translate("transportationPermission.quotaTimes.yearSuffix", "years");
      default:
        return '';
    }
  }

  get quotaUnit() {
    switch (this.transportationPermission.quotaType) {
      case 1:
        return this.destinationMovitInstanceConfig.isMileageInstance ? 'mi' : 'km';
      case 2:
        return this.destinationMovitInstanceConfig.currencySymbol;
      default:
        return this.$i18n.translate("transportationPermission.pcs", "pcs");
    }
  }

  get me() {
    return this.$store.state.user.me;
  }

  get routeDestinationId(): number {
    return parseInt(this.$store.state.route.params.destinationId, 10);
  }

  get destination(): Destination | null {
    if (this.routeDestinationId) {
      return this.$store.state.destination.destinations.find(d => d.id === this.routeDestinationId);
    }

    if (this.passenger.destinationIds.length > 0) {
      return this.$store.state.destination.destinations.find(d => d.id === this.passenger.destinationIds[0]);
    }
    return null;
  }

  get isEspoo() {
    if (this.destination && this.destination.priceArea === 0 || !this.passenger.organizationId) {
      return false;
    }
    const espooOrganizations: string[] = [
      '626a6858-8c19-4a69-b699-12e577ce012a'.toLowerCase(),
      '17dd2fb9-4253-4503-881d-f5cdb305ceaf'.toLowerCase(),
    ];

    return espooOrganizations.includes(this.passenger.organizationId.toLowerCase());
  }

  get storeMovitInstanceConfigs(): MovitInstanceConfig[] {
    return this.$store.state.app.movitInstanceConfigs as MovitInstanceConfig[];
  }

  get destinationMovitInstanceConfig(): MovitInstanceConfig | undefined {
    if (!this.destination) {
      return this.storeMovitInstanceConfigs.find(mic => mic.instanceId === this.passenger.movitInstanceId);
    }

    return this.storeMovitInstanceConfigs.find(mic => mic.instanceId === this.destination.movitInstanceId);
  }

  get newFeb2021FieldsSupported(): boolean {
    // New Feb2021 fields (#3490) are supported
    // if the destinations movitInstance returns a config object (commit 99ad46de)
    return this.destinationMovitInstanceConfig ? true : false;
  }

  get instanceConfigExtras(): Extra[] {
    return this.destinationMovitInstanceConfig
      ? this.destinationMovitInstanceConfig.extras
      : [];
  }

  get availableInstanceConfigExtras(): Extra[] {
    if (!this.showChangedIterator) {
      return [];
    }
    if (!this.transportationPermission || !this.transportationPermission.extras || this.transportationPermission.extras.length === 0) {
      return this.instanceConfigExtras;
    }
    return this.instanceConfigExtras.filter(
      (ice) =>
        this.transportationPermission.extras
          .map((e) => e.extraId)
          .indexOf(ice.id) === -1
    );
  }

  get passengerGroupPriceAreas() {
    const priceAreas = new PriceAreasTable();
    let excitingName;

    if (this.passengerGroups.length > 0 && this.destination.organizationId) {
      const destinationPriceArea = this.destination ? this.destination.priceArea : 0
      const priceAreasByGroupNames = priceAreas.getPriceAreasForPassengerGroups(this.destination.organizationId,
        this.passengerGroups.map((p) => p.name),
        destinationPriceArea
      );

      if (this.transportationPermission.name) {
        excitingName = priceAreasByGroupNames.find((a) => a.transportationPermissionName.toLowerCase() === this.transportationPermission.name.toLowerCase());
      }

      if (!excitingName && this.transportationPermission.name) {
        const temporaryPermission: IPriceArea = {
          transportationPermissionName: this.transportationPermission.name,
          passengerGroupName: "",
          priceAreas: [0, 0, 0, 0, 0, 0],
        };
        priceAreasByGroupNames.push(temporaryPermission);
      }

      return priceAreasByGroupNames.map((t) => t.transportationPermissionName);
    }

    const allPriceAreas = priceAreas.getPriceAreasForPassengerGroups(this.destination.organizationId);

    if (this.transportationPermission.name) {
      excitingName = allPriceAreas.find((p) => p.passengerGroupName.toLowerCase() === this.transportationPermission.name.toLowerCase());

      if (!excitingName) {
        const temporaryPermission: IPriceArea = {
          transportationPermissionName: this.transportationPermission.name,
          passengerGroupName: "",
          priceAreas: [0, 0, 0, 0, 0, 0],
        };
        allPriceAreas.push(temporaryPermission);
      }
    }

    return allPriceAreas.map((p) => p.transportationPermissionName);
  }

  get instanceConfigHasExtras(): boolean {
    return this.instanceConfigExtras && this.instanceConfigExtras.length > 0;
  }

  convertQuotaToUI() {
    const isMileageInstance = this.destinationMovitInstanceConfig
      ? this.destinationMovitInstanceConfig.isMileageInstance
      : false;

    if(this.transportationPermission) {
      this.transportationPermission.initialQuota = this.$options.filters?.quotaConvert(this.transportationPermission.initialQuota, this.transportationPermission.quotaType, isMileageInstance)
      this.transportationPermission.initialExpressQuota = this.$options.filters?.quotaConvert(this.transportationPermission.initialExpressQuota, this.transportationPermission.quotaType, isMileageInstance)
    }
  }

  setVatPercent() {
    if(!this.transportationPermission.priceVatPercent && this.transportationPermission.priceVatPercent !== 0) {
      this.transportationPermission.priceVatPercent = 0;
    }
  }

  removeExtra(extra: TransportationPermissionExtra) {
    let target: TransportationPermissionExtra | null;
    let index: number;
    if (extra.id > 0) {
      target = this.transportationPermission.extras.find(e => e.id === extra.id);
      index = this.transportationPermission.extras.indexOf(target);
      this.transportationPermission.extras.splice(index, 1);
    } else {
      target = this.transportationPermission.extras.find(e => e.name === extra.name && e.price === extra.price);
      index = this.transportationPermission.extras.indexOf(target);
      this.transportationPermission.extras.splice(index, 1);
    }
  }

  addExtra() {
    const newExtra = new TransportationPermissionExtra();
    if (this.selectedExtra || this.newExtra.name.length > 0) {
      newExtra.id = 0;
      newExtra.name = this.selectedExtra
        ? this.selectedExtra.name
        : this.newExtra.name;
      newExtra.price = this.selectedExtra
        ? this.selectedExtra.defaultPrice
        : this.newExtra.price;
      newExtra.extraId = this.selectedExtra ? this.selectedExtra.id : null;
      newExtra.passengerTransportationPermissionId =
        this.transportationPermission.id;

      this.transportationPermission.extras.push(newExtra);
      if (this.selectedExtra) {
        this.selectedExtra = null;
      }
      this.newExtra.name = "";
      this.newExtra.price = 0;
    }
  }

  get fieldNotSupportedInVersionNotificationText() {
    return this.$i18n.translate("destination.warning.field.notAvailable", "Field not available for destination");
  }

  get deductiblePaymentOptions() {
    return [
      {
        text: this.$i18n.translate("transportationPermission.deductiblePayment.car", "Driver"),
        value: DeductiblePaymentEnum.Car,
      },
      {
        text: this.$i18n.translate("transportationPermission.deductiblePayment.invoice", "Invoice"),
        value: DeductiblePaymentEnum.Invoice,
      },
    ];
  }

  get showDeductiblePaymentOptions(): boolean {
    if (!this.showChangedIterator) {
      return false;
    }

    return (
      this.transportationPermission &&
      this.transportationPermission.deductibleType !==
        DeductibleTypeEnum.NoDeductible
    );
  }

  get showPriceCodeField(): boolean {
    if (!this.showChangedIterator) {
      return false;
    }
    return (
      this.transportationPermission &&
      this.transportationPermission.deductibleType != DeductibleTypeEnum.FixedMonthly);
  }

  get showFixedDeductibleField(): boolean {
    if (!this.showChangedIterator) {
      return false;
    }
    return (
      this.transportationPermission &&
      (this.transportationPermission.deductibleType === DeductibleTypeEnum.Static ||
      this.transportationPermission.deductibleType === DeductibleTypeEnum.FixedMonthly)
    );
  }

  get deductibleTypeOptions() {
    return [
      {
        text: this.$i18n.translate(
          "transportationPermission.deductibleType.noDeductible",
          "No excess"
        ),
        value: DeductibleTypeEnum.NoDeductible,
      },
      {
        text: this.$i18n.translate(
          "transportationPermission.deductibleType.static",
          "Fixed amount"
        ),
        value: DeductibleTypeEnum.Static,
      },
      {
        text: this.$i18n.translate(
          "transportationPermission.deductibleType.matkahuolto",
          "Matkahuolto"
        ),
        value: DeductibleTypeEnum.Matkahuolto,
      },
      {
        text: this.$i18n.translate(
          "transportationPermission.deductibleType.walttiHame",
          "WalttiHäme"
        ),
        value: DeductibleTypeEnum.WalttiHäme,
      },
      {
        text: this.$i18n.translate(
          "transportationPermission.deductibleType.riihimaki",
          "Riihimäki"
        ),
        value: DeductibleTypeEnum.Riihimäki,
      },
    ];
  }

  setPrice(price: number) {
    this.transportationPermission.priceCode = price.toString();
    this.transportationPermission.priceIncVat = price;
    this.transportationPermission.priceVatPercent = 0;
  }

  async savePermission() {
    this.loading = true;
    if (this.transportationPermission.deductibleType !== DeductibleTypeEnum.Static &&
        this.transportationPermission.deductibleType !== DeductibleTypeEnum.FixedMonthly) {
      // clear out fixedDeductible if deductibleType is not static/fixed
      this.transportationPermission.fixedDeductible = null;
    }
    this.ConvertQuotaToDb();
    if (this.transportationPermission.id) {
      // update existing
      const result = await this.$store.dispatch(
        "passenger/updateTransportationPermission",
        {
          params: { passengerId: this.passenger.id, transportationPermissionId: this.transportationPermission.id },
          data: this.transportationPermission,
        }
      );
      const permission: TransportationPermission = new TransportationPermission(result.data);
      this.$emit("updatePermission", permission);
    } else {
      // create new
      await this.$store.dispatch("passenger/createTransportationPermission", {
        params: { passengerId: this.passenger.id },
        data: this.transportationPermission,
      });
    }

    if (this.passenger.organizationId) {
      await this.$store.dispatch(
        "organization/getPtpNamesForOrganization",
        { params: { id: this.passenger.organizationId } }
      );

      await this.$store.dispatch(
        "organization/getCostCentersForOrganization",
        { params: { id: this.passenger.organizationId } }
      );

    }

    this.loading = false;
    this.closeEditor();
  }

  async deletePermission() {
    if (!this.transportationPermission.id) {
      return;
    }
    if (!confirm(this.$i18n.translate('transportationPermission.deleteConfirm', 'Haluatko varmasti poistaa matkaoikeuden?'))) {
      return;
    }
    // delete existing
    try {
      const result = await this.$store.dispatch(
        "passenger/deleteTransportationPermission",
        {
          params: {
            passengerId: this.passenger.id,
            transportationPermissionId: this.transportationPermission.id,
          },
        }
      );
      this.$emit("onDelete", new TransportationPermission(result.data));
    } catch (err) {
      console.error(err);
      let message;
      if ((err as any).message.includes('Permission has reservations. Can not be deleted.')) {
        message = this.$i18n.translate('transportationPermission.error.hasPermission', 'Kuljetuspäätöksellä on matka varauksia eikä sitä voi poistaa.');
      } else {
        message = this.$i18n.translate('transportationPermission.error.failed', 'Kuljetupäätöksen poistaminen epäonnistui.');
      }
      vuetifyToast.error(message);
    }
    this.closeEditor();

  }

  closeEditor() {
    this.$emit("close");
    this.$emit("input", null);
  }

  created() {
    (window as any).__tpeditor = this;
  }
}
